import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { ErrorBoundary } from '@src/components/ErrorBoundary';
import {
  Box,
  Button,
  ButtonVariants,
  CardViewEnum,
  InternalButtonSpan,
  isMultileg,
  MarketDataCardState,
  MaxOrderSizeDialogs,
  MixpanelEvent,
  MarketDataCard as SharedMarketDataCard,
  Text,
  Tour,
  UnifiedLiquidityEnum,
  useCallbackRef,
  useMixpanel,
  useSecuritiesContext,
  useSecurity,
  useSyncedRef,
  VStack,
  type Security,
} from '@talos/kyoko';
import styled from 'styled-components';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { COLUMN_WIDTH } from '../../styles/dimensions';
import { TourName } from '../../tokens/tours';
import { useMarketCardsConfig } from '../AppLayout/Panels/Trading/MarketDataCardsPanel/MarketCardsConfigContext';
import { DeepDiveButtonElement } from '../DeepDive/DeepDiveButton';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import { MarketSelectorPanelOnly } from '../MarketSelector/MarketSelectorPanelOnly';
import { MarketCardMarketAccountButtonElement } from './MarketCardMarketAccountButton';
import { MarketDataCardHeader } from './MarketDataCardHeader';
import { MarketDataCardMarketSelectorProvider } from './MarketDataCardMarketSelectorContext';
import { MarketDataCardSettings } from './MarketDataCardSettings';
import { MarketDataCardSlides } from './MarketDataCardSlides';
import type { MarketDataCardProps } from './types';
import { useHandleClickMarketPrice } from './useHandleClickMarketPrice';
import { useHydratedMarketDataCardConfig } from './useHydratedMarketDataCardConfig';
import { useInitializedMarketAccountsConfig } from './useInitializedMarketAccountsConfig';
import {
  useMarketDataCardMarketSelector,
  type PersistMarketDataCardMarketSelection,
} from './useMarketDataCardMarketSelector';

export const MarketDataCard = ({ config, onSave, onDelete, ...props }: MarketDataCardProps) => {
  const hydratedConfig = useHydratedMarketDataCardConfig(config, onSave);
  return <InnerMarketDataCard config={hydratedConfig} onSave={onSave} onDelete={onDelete} {...props} />;
};

const InnerMarketDataCard = memo(function MarketDataCard({
  config,
  isDraggable,
  isDragged,
  defaultSubAccountId,
  onSave,
  onDelete,
  onHidePlaceholders,
  ...props
}: MarketDataCardProps) {
  const mixpanel = useMixpanel();
  const { forceCompact } = useMarketCardsConfig();
  const { useNewMarketSelector } = useTradingSettings();
  const { isLoaded } = useSecuritiesContext();
  const { isPopout } = usePanelContext();
  const isInMobileMode = useIsMobileLayout();
  const initializedConfig = useInitializedMarketAccountsConfig({ config, onSave, onDelete });
  const {
    detailsExpanded,
    symbol,
    view: configView,
    state,
    selectedMarketAccounts,
    selectedMarkets,
    unifiedLiquidity,
    selectedAggregation,
    orderPresetID,
    subAccount,
  } = initializedConfig;
  const view = forceCompact ? CardViewEnum.COMPACT : configView;
  const [showSettings, setShowSettings] = useState(state === MarketDataCardState.NEW_CARD);
  const [quickPanelOpen, setQuickPanelOpen] = useState(false);
  const [marketSelectorInventoryActive, setMarketSelectorInventoryActive] = useState(false);
  const resolvedSecurity: Security | undefined = useSecurity(symbol);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleToggleSettings = useCallback(() => {
    mixpanel.track(showSettings ? MixpanelEvent.CloseCardSettings : MixpanelEvent.OpenCardSettings);
    setShowSettings(!showSettings);
  }, [mixpanel, showSettings]);

  const showQuickMarketSelector = useNewMarketSelector && resolvedSecurity != null && !isMultileg(resolvedSecurity);

  const handleOpenMarketSelector = useCallback(() => {
    setMarketSelectorInventoryActive(true);
    setQuickPanelOpen(true);
  }, []);

  const handleCloseQuickPanel = useCallback(() => {
    setQuickPanelOpen(false);
  }, []);

  const ulEnabled = unifiedLiquidity === UnifiedLiquidityEnum.Enabled;
  const buildMarketSelectorInventory = showQuickMarketSelector && marketSelectorInventoryActive;

  const configRef = useSyncedRef(initializedConfig);

  const onPersistMarketSelection = useCallbackRef((update: PersistMarketDataCardMarketSelection) => {
    onSave({
      ...configRef.current,
      selectedAggregation: update.selectedAggregation,
      selectedMarkets: update.selectedMarkets,
      selectedMarketAccounts: update.selectedMarketAccounts,
      availableMarkets: update.selectedMarkets,
      availableMarketAccounts: update.selectedMarketAccounts,
    });
  });

  const {
    marketSelector,
    oldMarketSelectorItems,
    marketSelectorItems,
    selectedMarketOrAccountNames,
    selectedAggregationID,
    selectedAggregationLabel,
    favoriteAggregationPresets,
    selectAggregationPreset,
  } = useMarketDataCardMarketSelector({
    security: resolvedSecurity,
    ulEnabled,
    selectedMarkets,
    selectedMarketAccounts,
    selectedAggregation,
    onPersistMarketSelection,
    buildMarketSelectorInventory,
  });

  const marketSelectorContextValue = useMemo(
    () => ({
      marketSelector,
      oldMarketSelectorItems,
      marketSelectorItems,
      selectedMarketOrAccountNames,
      selectedAggregationID,
      selectedAggregationLabel,
      favoriteAggregationPresets,
      selectAggregationPreset,
    }),
    [
      marketSelector,
      oldMarketSelectorItems,
      marketSelectorItems,
      selectedMarketOrAccountNames,
      selectedAggregationID,
      selectedAggregationLabel,
      favoriteAggregationPresets,
      selectAggregationPreset,
    ]
  );

  const {
    clickToTradePreviewOrder,
    onClickPrice: handleClickPrice,
    onCancelActive: handleCancelActive,
    maxOrderSizeDialogs,
  } = useHandleClickMarketPrice({
    symbol,
    unifiedLiquidity,
    subAccount,
    defaultSubAccountId,
    orderPresetID,
    marketAccounts: selectedMarketAccounts,
    markets: selectedMarkets,
    aggregation: selectedAggregation,
  });

  const handleDeleteCard = useCallback(() => {
    onDelete(initializedConfig);
    if (state === MarketDataCardState.NEW_CARD) {
      onHidePlaceholders();
    }
  }, [initializedConfig, onDelete, onHidePlaceholders, state]);

  const showDelete = useMemo(
    () => !showSettings && resolvedSecurity == null && isLoaded,
    [showSettings, resolvedSecurity, isLoaded]
  );

  return (
    <Wrapper {...props} ref={cardRef}>
      <MarketDataCardMarketSelectorProvider value={marketSelectorContextValue}>
        <SharedMarketDataCard
          ErrorBoundary={ErrorBoundary}
          isDragged={isDragged}
          isExpanded={!!detailsExpanded}
          view={showSettings ? CardViewEnum.NORMAL : view}
        >
          {showDelete ? (
            <VStack gap="spacing8" h="100%" justifyContent="center" alignItems="center">
              <Text>Cannot find symbol {initializedConfig.symbol}</Text>
              <Button onClick={handleDeleteCard} variant={ButtonVariants.Negative}>
                Delete Card
              </Button>
            </VStack>
          ) : showSettings ? (
            <MarketDataCardSettings
              config={initializedConfig}
              onClose={handleToggleSettings}
              saveCard={onSave}
              deleteCard={onDelete}
              hidePlaceholderCards={onHidePlaceholders}
              cardRef={cardRef}
            />
          ) : (
            <>
              <MarketDataCardHeader
                isDraggable={isDraggable}
                config={initializedConfig}
                security={resolvedSecurity}
                activeOrder={clickToTradePreviewOrder}
                onClickSettings={handleToggleSettings}
                onClickPrice={handleClickPrice}
                onCancelActive={handleCancelActive}
                onSaveCard={onSave}
                onDelete={onDelete}
                defaultSubAccountId={defaultSubAccountId}
                onOpenMarketSelector={showQuickMarketSelector ? handleOpenMarketSelector : undefined}
              />
              {showQuickMarketSelector && <Tour tour={TourName.v257MarketCardUpdate} />}
              {view !== CardViewEnum.COMPACT && (
                <MarketDataCardSlides
                  config={initializedConfig}
                  security={resolvedSecurity}
                  activeOrder={clickToTradePreviewOrder}
                  onClickPrice={handleClickPrice}
                  onCancelActive={handleCancelActive}
                  onSaveCard={onSave}
                  onOpenMarketSelector={showQuickMarketSelector ? handleOpenMarketSelector : undefined}
                />
              )}
              {showQuickMarketSelector && (
                <MarketSelectorPanelOnly
                  marketSelectorAPI={marketSelector}
                  isOpen={quickPanelOpen}
                  onClose={handleCloseQuickPanel}
                  panelAnchorRef={cardRef}
                  isNarrow={isPopout || isInMobileMode}
                  showCredentialsOnlyToggle
                />
              )}
              <MaxOrderSizeDialogs {...maxOrderSizeDialogs} />
            </>
          )}
        </SharedMarketDataCard>
      </MarketDataCardMarketSelectorProvider>
    </Wrapper>
  );
});

const Wrapper = styled(Box)`
  min-width: ${COLUMN_WIDTH}px;
  &:not(:hover) {
    .order-presets-prev,
    .order-presets-next,
    .subaccount-prev,
    .subaccount-next,
    .aggregation-prev,
    .aggregation-next {
      display: none;
    }
  }

  /** Keep icon-only controls visually compact until interaction reveals their text/span content. */
  ${DeepDiveButtonElement}:not(:hover):not(:focus-within),
  ${MarketCardMarketAccountButtonElement}:not(:hover):not(:focus-within) {
    ${InternalButtonSpan} {
      display: none;
    }
  }

  ${DeepDiveButtonElement},
  ${MarketCardMarketAccountButtonElement} {
    ${InternalButtonSpan} {
      /** Let the height of the icon determine the height of the button */
      height: 0px;
    }
  }
`;
